import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight, Check, Zap, Layers, Projector, Cpu, FileText,
  Gauge, Lightbulb, Aperture, Camera, Image as ImageIcon,
  ScanLine, Sun, Thermometer, Grid3x3, Box, Blocks, Workflow,
  FileDown, MonitorPlay, PlayCircle, Instagram, Mail,
  LogOut, User as UserIcon,
} from 'lucide-react';
import { useInView } from './useInViewV2';
import { useAuth } from '../../context/AuthContext';
import './landingV2.css';

/* ========================================================================
   Data — one object per feature row / image. Images live in
   /app/frontend/public/landing/*.webp (1.6 MB total after WebP compression)
   ======================================================================== */

const HERO_IMAGE = '/landing/immersive-projection.webp';

const FEATURES_STICKY = [
  {
    id: 'projection',
    tag: 'Projection system',
    title: 'Real-time projection mapping',
    copy: 'Simulate building facades, domes, and freeform surfaces with accurate light falloff, multi-projector blending, and overlap brightness control.',
    img: '/landing/building-projection.webp',         // Building Projection.png
  },
  {
    id: 'projectors',
    tag: 'Projectors · stacking',
    title: 'Full projector library with stack support',
    copy: 'Barco, Panasonic, Christie, Epson, Sharp/NEC, Optoma, BenQ, Digital Projection — real lumens, real throw ratios. Stack projectors vertically or laterally for doubled brightness.',
    img: '/landing/stacking-projectors.webp',         // stacking projection .png
  },
  {
    id: 'reverse',
    tag: 'Reverse projection',
    title: 'Rear-projection workflows',
    copy: 'Flip the projection axis for rear-throw scrims and translucent surfaces — get accurate hotspots, falloff, and lens-shift behaviour without guesswork.',
    img: '/landing/reverse-projection.webp',          // Reverse Projection.png
  },
  {
    id: 'lens',
    tag: 'Lens system',
    title: 'Manufacturer-accurate lenses (incl. 90° UST)',
    copy: 'Every projector ships with its real lens lineup — throw ratios, 90° UST short-throws, and per-lens shift ranges. Pick the lens the same way your rigger does.',
    img: '/landing/lens-90.webp',                     // 90 degree Lens.png
  },
  {
    id: 'scrim-360',
    tag: 'Scrim · 360°',
    title: '360° scrim projection tunnels',
    copy: 'Wrap scrim screens in a full 360° layout and see how beams travel, pass through, and recombine on the back wall.',
    img: '/landing/scrim-360.webp',                   // Scrim 360 degree Proejction.png
  },
  {
    id: 'scrim-multi',
    tag: 'Scrim · multi-layer',
    title: 'Multi-layer scrim depth',
    copy: 'Transparent scrim layers with depth stacking — build immersive proscenium reveals and pre-visualise exactly what the audience sees front-of-house.',
    img: '/landing/scrim-multiple.webp',              // Multiple scrim Screen Proejction.png
  },
  {
    id: 'pov',
    tag: 'POV · camera',
    title: 'Fly the venue from any angle',
    copy: 'First-person POV cameras, orbit rigs, saved views — walk through the show the way your audience will, frame the hero shot before the truss is on trucks.',
    img: '/landing/pov-view.webp',                    // POV view .png
  },
  {
    id: 'texture',
    tag: 'Texture · UV mapping',
    title: 'Universal texture mapping',
    copy: 'Auto / UV / Triplanar modes handle poorly-UV\'d 3D models out of the box — triplanar wrapping means no seams, no stretched pixels, no prep.',
    img: '/landing/uv-mapping.webp',                  // UV Mapping.png
  },
  {
    id: 'shadows',
    tag: 'Real shadows',
    title: 'Physically-accurate shadow casting',
    copy: 'Trees, trusses, scaffolds and human models cast proper shadows onto projection surfaces. See occlusion problems before your onsite rehearsal.',
    img: '/landing/real-shadows.webp',                // Tree Proejction.png
  },
  {
    id: 'heatmap',
    tag: 'Lux heatmap',
    title: 'Precise lux visualisation',
    copy: 'Colour-graded lux heatmaps per projector, per stack, per overlap zone — prove your show is safe, uniform, and hitting spec before you ship cases.',
    img: '/landing/heatmap.webp',                     // heatmap visulastion.png
  },
  {
    id: 'ortho',
    tag: 'Ortho view',
    title: 'Plan drawings in one click',
    copy: 'Switch to orthographic top / front / side views for plans, elevations, and engineering reports — perfect for stage managers and load-in crews.',
    img: '/landing/ortho-view.webp',                  // Ortho view .png
  },
];

const SPECIALIST_ROWS = [
  {
    id: 'led',
    eyebrow: 'LED system',
    title: 'Curved, flat, and 90° LED walls',
    copy: 'Build any wall topology — flat video walls, 90° corner wraps, curved arcs, holographic mesh. Mix and match with projection for hybrid shows.',
    img: '/landing/led-projection-mapping.webp',      // LED with proejction maping.png
    bullets: ['Flat · Curve · 90° corner wraps', 'Per-pixel bezel preview', 'Hybrid LED + projection pipelines'],
    flip: false,
  },
  {
    id: 'led-holo',
    eyebrow: 'Holographic projection + LED',
    title: 'Holographic projection with LED staging',
    copy: 'Combine holographic projection with curved LED walls and overhead truss-mounted projectors — simulate the full staging setup in a single scene.',
    img: '/landing/led-holography.webp',              // LED with Holography.png
    bullets: ['Truss', 'Curved LED wall', 'Holographic projection with truss'],
    flip: true,
  },
  {
    id: 'holography',
    eyebrow: 'Holography',
    title: 'Holographic curved screen',
    copy: 'A curved holographic screen mounted inside a truss frame, fed by overhead projection — the classic volumetric reveal layout in a single scene.',
    img: '/landing/holography.webp',                  // Holography.png
    bullets: ['Holographic curved screen', 'Truss', 'Projection'],
    flip: false,
  },
  {
    id: 'scaffolding',
    eyebrow: 'Scaffolding',
    title: 'Modular ringlock scaffolding',
    copy: 'Design support towers with accurate ringlock geometry, realistic decking, and 3D-correct pipe cross-sections. Built for AV, not construction clip-art.',
    img: '/landing/scaffolding.webp',                 // Scaffolding .png
    bullets: ['Ringlock towers to 200 m', 'Deck + stair placement per level', 'Aluminium / steel materials'],
    flip: true,
  },
  {
    id: 'scaffolding-load',
    eyebrow: 'Scaffolding · load check',
    title: 'Real-time load-capacity calculation',
    copy: 'Enter top load and per-side loads — get an instant pass/fail readout against the tower\'s modular rating. Know what your rig weighs before the truck leaves.',
    img: '/landing/scaffolding-load.webp',            // Scafolding Load check.png
    bullets: ['Per-deck + side load inputs', 'Material-aware capacity', 'One-click engineering report'],
    flip: false,
  },
  {
    id: 'simulation',
    eyebrow: 'Simulation · 3D import',
    title: 'Bring your own venue',
    copy: 'Drop in FBX, OBJ, GLB or STL files — stages, buildings, props. Auto-conversion handles the format wars, triplanar mapping handles the UVs.',
    img: '/landing/dome-projection.webp',             // Dome Projection.png (3D dome surface)
    bullets: ['FBX / OBJ / GLB / STL', 'Client-side format conversion', 'Universal triplanar wrapping'],
    flip: true,
  },
  {
    id: 'exports',
    eyebrow: 'Export · reports',
    title: 'Client-ready deliverables',
    copy: 'One-click PNG renders for pitch decks, full PDF rigging reports with per-projector specs, lux values, and shadow occupancy — the paperwork handled.',
    img: '/landing/immersive-projection.webp',        // Immershive Proejction.png (polished "ready to ship" look)
    bullets: ['High-res PNG / JPEG', 'Structured PDF reports', 'Shareable read-only viewer links'],
    flip: false,
  },
];

/* Quick-strip icons row beneath hero */
const QUICK_STRIP = [
  { label: 'Projection', Icon: Projector },
  { label: 'LED', Icon: MonitorPlay },
  { label: 'Truss', Icon: Layers },
  { label: 'Simulation', Icon: Box },
  { label: 'Reports', Icon: FileDown },
];

/* Section dots — one per scrollable anchor on the page */
const DOTS = [
  { id: 'hero',             label: 'Home' },
  { id: 'about',            label: 'About' },
  { id: 'features',         label: 'Features' },
  { id: 'led',              label: 'LED' },
  { id: 'led-holo',         label: 'Holo + LED' },
  { id: 'holography',       label: 'Holography' },
  { id: 'scaffolding',      label: 'Scaffolding' },
  { id: 'scaffolding-load', label: 'Load Check' },
  { id: 'simulation',       label: 'Simulation' },
  { id: 'exports',          label: 'Exports' },
  { id: 'combined',         label: 'All-in-one' },
  { id: 'cta',              label: 'Get started' },
];

/* ---------------- Small building blocks ---------------- */

const Reveal = ({ children, as: Tag = 'div', className = '', delay = 0, ...rest }) => {
  const [ref, inView] = useInView();
  return (
    <Tag
      ref={ref}
      className={`lpv2-reveal ${inView ? 'is-in' : ''} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

/* Lightbox (UPGRADE D) */
const Lightbox = ({ src, alt, onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);
  return (
    <div className="lpv2__lightbox" onClick={onClose} data-testid="landing-lightbox">
      <button
        type="button"
        className="lpv2__lightbox-close"
        onClick={onClose}
        data-testid="landing-lightbox-close"
      >Close · Esc</button>
      <img src={src} alt={alt} onClick={(e) => e.stopPropagation()} />
    </div>
  );
};

/* ---------------- Sticky feature scroller (UPGRADE A) ---------------- */

const StickyFeatures = ({ onOpen }) => {
  const containerRef = useRef(null);
  const itemRefs = useRef([]);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        // Pick the last entry that's currently intersecting (closest to the
        // top of the viewport) as the active feature.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible.length) {
          const idx = itemRefs.current.indexOf(visible[0].target);
          if (idx !== -1) setActiveIdx(idx);
        }
      },
      { rootMargin: '-40% 0px -40% 0px', threshold: [0, 0.5, 1] }
    );
    itemRefs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="lpv2__sticky">
      {/* Shared sticky visual — wrapped in a full-height column so the
          sticky element has the entire grid cell as its "sticky range".
          Without this wrapper the visual only gets its intrinsic 16:10
          height, which is also its sticky limit, so it unpins immediately. */}
      <div className="lpv2__sticky-visual-col">
        <div className="lpv2__sticky-visual" data-testid="sticky-feature-visual">
          {FEATURES_STICKY.map((f, i) => (
            <img
              key={f.id}
              src={f.img}
              alt={f.title}
              loading="lazy"
              decoding="async"
              className={i === activeIdx ? 'is-active' : ''}
              onClick={() => onOpen({ src: f.img, alt: f.title })}
            />
          ))}
        </div>
      </div>

      <div className="lpv2__sticky-list">
        {FEATURES_STICKY.map((f, i) => (
          <div
            key={f.id}
            ref={(el) => (itemRefs.current[i] = el)}
            className={`lpv2__sticky-item ${i === activeIdx ? 'is-active' : ''}`}
            data-testid={`sticky-feature-item-${f.id}`}
          >
            {/* Mobile-only inline image (sticky disabled <= 900px) */}
            <img
              src={f.img}
              alt={f.title}
              loading="lazy"
              decoding="async"
              className="lpv2__sticky-item-image"
              onClick={() => onOpen({ src: f.img, alt: f.title })}
            />
            <span className="lpv2__sticky-item-tag">{f.tag}</span>
            <h3>{f.title}</h3>
            <p>{f.copy}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---------------- Page ---------------- */

const LandingPageV2 = () => {
  const [progress, setProgress] = useState(0);
  const [activeDot, setActiveDot] = useState('hero');
  const [lightbox, setLightbox] = useState(null);
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const firstName = user?.name?.split(' ')[0] || user?.email?.split('@')[0] || '';

  /* Unlock page-level scroll — the global `body { overflow: hidden }` in
     `index.css` is required by the editor (which manages its own scroll
     regions). We need to override it while the landing is mounted, but
     WITHOUT creating a scroll container on body: `overflow: auto` would
     make body the scrolling ancestor and then `position: sticky` inside
     would break (sticky would pin against body's scroll, not the document,
     and since body doesn't have its own overflow, sticky can't compute
     anything and stays un-pinned). `visible` is the right unlock — the
     document scrolls, body doesn't clip, and sticky works as expected. */
  useEffect(() => {
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = 'visible';
    document.documentElement.style.overflow = 'visible';
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, []);

  /* Global scroll progress (top bar) + active section (right dots) */
  useEffect(() => {
    const update = () => {
      const sd = document.documentElement;
      const total = sd.scrollHeight - sd.clientHeight;
      const p = total > 0 ? window.scrollY / total : 0;
      setProgress(p);

      // Active section: whichever is closest to 1/3 of the viewport
      const anchor = window.scrollY + window.innerHeight / 3;
      let best = DOTS[0].id;
      let bestDist = Infinity;
      for (const d of DOTS) {
        const el = document.getElementById(d.id);
        if (!el) continue;
        const top = el.offsetTop;
        const dist = Math.abs(top - anchor);
        if (dist < bestDist) { bestDist = dist; best = d.id; }
      }
      setActiveDot(best);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  /* Lazy-load the CAR mapping video only when its section hits the viewport */
  const [videoRef, videoInView] = useInView({ threshold: 0.18, rootMargin: '100px' });
  const videoElRef = useRef(null);
  useEffect(() => {
    const el = videoElRef.current;
    if (!el || !videoInView) return;
    if (!el.src) {
      el.src = '/landing/car-mapping.mp4';
      el.load();
      el.play().catch(() => { /* autoplay blocked is fine, poster shows */ });
    }
  }, [videoInView]);

  /* Smooth scroll helper */
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const heroStyle = useMemo(() => ({ '--hero-image': `url(${HERO_IMAGE})` }), []);

  return (
    <div className="lpv2" style={{ '--progress': progress }} data-testid="landing-v2">
      {/* Progress bar + dots */}
      <div className="lpv2__progress" aria-hidden="true" />
      <nav className="lpv2__dots" aria-label="Section navigation">
        {DOTS.map((d) => (
          <button
            key={d.id}
            type="button"
            data-label={d.label}
            className={activeDot === d.id ? 'is-active' : ''}
            onClick={() => scrollTo(d.id)}
            data-testid={`landing-dot-${d.id}`}
            aria-label={`Jump to ${d.label}`}
          />
        ))}
      </nav>

      {/* Top nav */}
      <header className="lpv2__nav" data-testid="landing-nav">
        <Link to="/" className="lpv2__brand" data-testid="landing-brand-link" style={{ textDecoration: 'none', color: 'inherit' }}>
          <img
            src="/branding/avst-logo.png"
            alt=""
            className="lpv2__brand-logo"
            draggable={false}
            style={{ height: 32, width: 'auto', filter: 'drop-shadow(0 0 12px rgba(56,189,248,0.35))' }}
          />
          <span>AV Simulation Studio</span>
        </Link>
        <div className="lpv2__nav-links">
          <a onClick={(e) => { e.preventDefault(); scrollTo('features'); }} href="#features">Features</a>
          <a onClick={(e) => { e.preventDefault(); scrollTo('led'); }} href="#led">LED</a>
          <a onClick={(e) => { e.preventDefault(); scrollTo('scaffolding'); }} href="#scaffolding">Scaffolding</a>
          <a onClick={(e) => { e.preventDefault(); scrollTo('exports'); }} href="#exports">Exports</a>
        </div>
        <div className="lpv2__nav-actions">
          <a
            href="https://www.instagram.com/avsimulationtool/"
            target="_blank"
            rel="noreferrer"
            aria-label="AV Simulation Tool on Instagram"
            className="lpv2__nav-ig"
            data-testid="landing-nav-instagram"
          >
            <Instagram size={18} />
          </a>
          {/* Simulation + Test Pattern + Admin are ALWAYS visible.
              Simulation gates on auth (→ /login if guest, /app if signed in);
              Test Pattern is always open (utility is free for everyone).
              Admin Panel only renders when the signed-in user is admin. */}
          <button
            type="button"
            className="lpv2__btn lpv2__btn--ghost"
            onClick={() => navigate(isAuthenticated ? '/app' : '/login', isAuthenticated ? undefined : { state: { from: '/app' } })}
            data-testid="landing-nav-simulation"
          >
            <Box size={14} /> Simulation
          </button>
          <Link to="/tools/test-pattern" className="lpv2__btn lpv2__btn--ghost" data-testid="landing-nav-tpg">
            <Grid3x3 size={14} /> Pattern &amp; Blend
          </Link>
          <Link to="/tools/av-designer" className="lpv2__btn lpv2__btn--ghost" data-testid="landing-nav-avd">
            <Workflow size={14} /> AV System Designer
          </Link>
          <Link to="/tools/led-pattern" className="lpv2__btn lpv2__btn--ghost" data-testid="landing-nav-led">
            <MonitorPlay size={14} /> LED Pattern &amp; Output
          </Link>
          {isAuthenticated && user?.role === 'admin' && (
            <Link to="/admin" className="lpv2__btn lpv2__btn--ghost lpv2__btn--mobile-admin" data-testid="landing-nav-admin">
              <UserIcon size={14} /> <span className="lpv2__nav-admin-label">Admin Panel</span>
            </Link>
          )}
          {isAuthenticated ? (
            <>
              <span className="lpv2__user-pill" data-testid="landing-user-pill" title={user?.email || ''}>
                <UserIcon size={13} /> Hi, {firstName || 'there'}
              </span>
              <button
                type="button"
                className="lpv2__btn lpv2__btn--primary"
                onClick={() => { logout(); navigate('/'); }}
                data-testid="landing-nav-logout"
              >
                <LogOut size={14} /> Sign out
              </button>
            </>
          ) : (
            <>
              <Link to="/signup" className="lpv2__btn lpv2__btn--ghost" data-testid="landing-nav-trial">
                Free Demo
              </Link>
              <Link to="/login" className="lpv2__btn lpv2__btn--primary" data-testid="landing-nav-login">
                Sign in <ArrowRight size={14} />
              </Link>
            </>
          )}
        </div>
      </header>

      {/* ---------- HERO ---------- */}
      <section id="hero" className="lpv2__hero" style={heroStyle}>
        <div className="lpv2__hero-bg" aria-hidden="true" />
        <div>
          <Reveal className="lpv2__hero-kicker">
            {isAuthenticated
              ? `Welcome back${firstName ? `, ${firstName}` : ''} · choose a workspace`
              : 'AV · Projection · Simulation'}
          </Reveal>
          <Reveal as="h1" delay={80}>
            {isAuthenticated ? (
              <>What would you like to <span className="grad">open</span> today?</>
            ) : (
              <>Plan AV systems. <span className="grad">Simulate projections.</span> Calibrate on-site.</>
            )}
          </Reveal>
          <Reveal as="p" className="lpv2__hero-tagline" delay={160}>
            {isAuthenticated
              ? 'Two purpose-built workspaces — the full 3D Simulation editor for projection mapping, truss & LED, and the standalone Pattern & Blend tool for on-site calibration and multi-projector edge blending.'
              : 'Design, simulate, and visualise complete AV environments in real-time — projection, LED, truss, scaffolding, and everything in between.'}
          </Reveal>
          <Reveal as="p" className="lpv2__hero-dse" delay={220}>
            <span>Design.</span><span className="dot">·</span>
            <span>Simulate.</span><span className="dot">·</span>
            <span>Calibrate.</span>
          </Reveal>

          <Reveal className="lpv2__chips" delay={280}>
            <span className="lpv2__chip"><Check /> Real-world projection accuracy</span>
            <span className="lpv2__chip"><Check /> Multi-system simulation — LED · Truss · Scaffolding</span>
            <span className="lpv2__chip"><Check /> Instant visualisation & export</span>
          </Reveal>

          <Reveal className="lpv2__hero-ctas" delay={360}>
            {/* Both workspace CTAs are ALWAYS shown.
                Simulation goes to /login for guests, /app for signed-in users.
                Test Pattern is always open. */}
            <button
              type="button"
              className="lpv2__btn lpv2__btn--primary"
              onClick={() => navigate(isAuthenticated ? '/app' : '/login', isAuthenticated ? undefined : { state: { from: '/app' } })}
              data-testid="landing-hero-simulation"
            >
              <Box size={16} /> {isAuthenticated ? 'Open Simulation' : 'Sign in to open Simulation'}
            </button>
            <Link to="/tools/test-pattern" className="lpv2__btn" data-testid="landing-hero-tpg">
              <Grid3x3 size={16} /> Open Pattern &amp; Blend
            </Link>
            {!isAuthenticated && (
              <Link to="/signup" className="lpv2__btn lpv2__btn--ghost" data-testid="landing-cta-start">
                Start 15-Day Free Demo <ArrowRight size={16} />
              </Link>
            )}
          </Reveal>
          <Reveal className="lpv2__hero-microcopy" delay={420}>
            <span data-testid="landing-trial-note">
              {isAuthenticated
                ? <><Check /> Signed in as {user?.email}</>
                : <><Check /> Full access for 15 days · No credit card required</>}
            </span>
          </Reveal>
        </div>
      </section>

      {/* ---------- Workspace tabs — ALWAYS visible.
           Three professional workspaces live in the platform now:
           Simulation (3D), Test Pattern Generator, and AV System Designer.
           Admin appears as a 4th card only when the user is an administrator.
           Simulation gates on auth (→ /login if guest, /app if signed in);
           Test Pattern and AV System Designer are open to everyone. ----- */}
      <section className="lpv2__section" aria-labelledby="workspace-heading" data-testid="landing-workspaces">
        <Reveal className="lpv2__eyebrow">Workspaces</Reveal>
        <Reveal as="h2" id="workspace-heading" delay={60}>
          Pick your <span className="grad">workspace</span>
        </Reveal>
        <div className="lpv2__workspaces lpv2__workspaces--three">
          <button
            type="button"
            className="lpv2__workspace"
            onClick={() => navigate(isAuthenticated ? '/app' : '/login', isAuthenticated ? undefined : { state: { from: '/app' } })}
            data-testid="landing-workspace-simulation"
          >
            <div className="lpv2__workspace-media">
              <img src="/landing/immersive-projection.webp" alt="3D Simulation workspace preview" loading="lazy" decoding="async" />
              <span className="lpv2__workspace-chip"><Box size={14} /> 3D Workspace</span>
            </div>
            <div className="lpv2__workspace-body">
              <h3>Simulation</h3>
              <p>The full Babylon-powered editor — projection mapping, truss, scaffolding, LED walls, lens libraries, lux &amp; shadow simulation, share links.</p>
              <span className="lpv2__workspace-cta">
                {isAuthenticated ? 'Open Simulation' : 'Sign in to open'} <ArrowRight size={16} />
              </span>
            </div>
          </button>
          <Link to="/tools/test-pattern" className="lpv2__workspace" data-testid="landing-workspace-tpg">
            <div className="lpv2__workspace-media">
              <img src="/landing/pattern-blend-preview.png" alt="Pattern & Blend test-pattern grid preview" loading="lazy" decoding="async" />
              <span className="lpv2__workspace-chip"><Grid3x3 size={14} /> Standalone 2D · No sign-in</span>
            </div>
            <div className="lpv2__workspace-body">
              <h3>Pattern &amp; Blend</h3>
              <p>Lightweight calibration tool — test patterns, multi-projector edge blending, lux readouts, auto-calc overlaps, native-resolution PNG/PDF exports, multi-tab projects, cloud save when signed in.</p>
              <span className="lpv2__workspace-cta">Open Pattern &amp; Blend <ArrowRight size={16} /></span>
            </div>
          </Link>
          <Link to="/tools/av-designer" className="lpv2__workspace lpv2__workspace--avd" data-testid="landing-workspace-avd">
            <div className="lpv2__workspace-media">
              <img src="/landing/avd-signal-flow.png" alt="AV System Designer signal-flow canvas preview" loading="lazy" decoding="async" />
              <span className="lpv2__workspace-chip"><Workflow size={14} /> Signal Flow · Wiring · Racks</span>
            </div>
            <div className="lpv2__workspace-body">
              <h3>AV System Designer</h3>
              <p>Professional AV infrastructure planner — drag equipment, wire signal flows, plan racks, validate ports, export to PDF/PNG. JSON-editable library, multi-project tabs, auto-save.</p>
              <span className="lpv2__workspace-cta">Open AV Designer <ArrowRight size={16} /></span>
            </div>
          </Link>
          <Link to="/tools/led-pattern" className="lpv2__workspace lpv2__workspace--led" data-testid="landing-workspace-led">
            <div className="lpv2__workspace-media">
              <img src="/landing/led-pattern-preview.png" alt="LED Pattern & Output Assignment preview" loading="lazy" decoding="async" />
              <span className="lpv2__workspace-chip"><MonitorPlay size={14} /> LED Mapping · Processors · Outputs</span>
            </div>
            <div className="lpv2__workspace-body">
              <h3>LED Pattern &amp; Output</h3>
              <p>Standalone LED calibration tool — build pixel-accurate LED walls, assign cabinets to processor outputs, render test patterns (RGB, checker, gray ramp, brightness), snap-to-edge layout, PDF reports.</p>
              <span className="lpv2__workspace-cta">Open LED Pattern <ArrowRight size={16} /></span>
            </div>
          </Link>
        </div>
      </section>

      {/* ---------- Quick feature strip ---------- */}
      <div className="lpv2__strip" aria-label="At a glance">
        <div className="lpv2__strip-inner">
          {QUICK_STRIP.map(({ label, Icon }) => (
            <div key={label} className="lpv2__strip-item">
              <Icon size={16} />
              <span>{label}</span>
            </div>
          ))}
          <div className="lpv2__strip-item" style={{ color: 'var(--text)' }}>
            <Zap size={16} />
            <span>No plugin, no download — runs in your browser.</span>
          </div>
        </div>
      </div>

      {/* ---------- About ---------- */}
      <section id="about" className="lpv2__section">
        <div className="lpv2__section-inner">
          <Reveal><div className="lpv2__eyebrow">What this app does</div></Reveal>
          <Reveal as="h2" delay={60}>
            A complete platform to design <span style={{ background: 'var(--brand-grad)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>
            projection mapping, LED setups, truss systems, and full event environments</span> with real-world accuracy.
          </Reveal>
          <Reveal as="p" className="lpv2__lead" delay={140}>
            AV Simulation replaces the guesswork of sketch-and-hope rigging with an interactive browser tool where lenses, lumens, lens-shifts, truss weights, and shadow occlusion are all measured the way your engineers measure them on site.
          </Reveal>
        </div>
      </section>

      {/* ---------- Sticky feature scroll ---------- */}
      <section id="features" className="lpv2__section">
        <div className="lpv2__section-inner" style={{ marginBottom: 48 }}>
          <Reveal><div className="lpv2__eyebrow">Core features</div></Reveal>
          <Reveal as="h2" delay={60}>Ten systems. One canvas. Zero guesswork.</Reveal>
        </div>
        <StickyFeatures onOpen={setLightbox} />
      </section>

      {/* ---------- LED ---------- */}
      {SPECIALIST_ROWS.map((row) => (
        <section key={row.id} id={row.id} className="lpv2__section">
          <div className={`lpv2__row ${row.flip ? 'lpv2__row--flip' : ''}`}>
            <Reveal
              className="lpv2__row-visual"
              onClick={() => setLightbox({ src: row.img, alt: row.title })}
              data-testid={`landing-row-${row.id}`}
            >
              <img src={row.img} alt={row.title} loading="lazy" decoding="async" />
            </Reveal>
            <Reveal className="lpv2__row-copy" delay={80}>
              <div className="lpv2__eyebrow">{row.eyebrow}</div>
              <h3>{row.title}</h3>
              <p>{row.copy}</p>
              {row.bullets?.length > 0 && (
                <ul>
                  {row.bullets.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              )}
            </Reveal>
          </div>
        </section>
      ))}

      {/* ---------- Combined visualisation + video ---------- */}
      <section id="combined" className="lpv2__section">
        <div className="lpv2__section-inner" style={{ marginBottom: 40, textAlign: 'center' }}>
          <Reveal><div className="lpv2__eyebrow">All systems together</div></Reveal>
          <Reveal as="h2" delay={60} style={{ margin: '0 auto 14px' }}>
            See every system talking to every other system.
          </Reveal>
          <Reveal as="p" className="lpv2__lead" delay={140} style={{ margin: '0 auto' }}>
            Projection across scrim screens while LED walls flank the stage, truss and scaffolding
            lit and shadow-casting in real time. Not concept art — your actual rig.
          </Reveal>
        </div>
        <div ref={videoRef} className="lpv2__video" data-testid="landing-video">
          <video
            ref={videoElRef}
            poster="/landing/car-mapping-poster.webp"
            autoPlay
            muted
            loop
            playsInline
            preload="none"
          />
        </div>
      </section>

      {/* ---------- AV System Designer showcase ---------- */}
      <section className="lpv2__section" aria-labelledby="avd-heading">
        <div className="lpv2__tpg" data-testid="landing-avd-cta">
          <div className="lpv2__tpg-copy">
            <div className="lpv2__tpg-eyebrow">New · Standalone Workspace</div>
            <h2 id="avd-heading">
              AV System <span style={{
                background: 'var(--brand-grad)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent'
              }}>Designer</span>
            </h2>
            <p>
              Map the entire backstage in one canvas — every projector, media server, switcher,
              processor, network switch and power distro wired exactly the way it will be on show day.
              Drag devices from a 200+ unit library, snap connections port-to-port, and let the
              signal-flow engine catch every mismatch before it bites you at load-in.
            </p>
            <div className="lpv2__tpg-bullets">
              <div className="lpv2__tpg-bullet">Visual signal flow — HDMI · SDI · DisplayPort · Fiber · CAT6 / LAN · Genlock · LTC · Power</div>
              <div className="lpv2__tpg-bullet">Port-aware validation — passive vs active converters flagged automatically</div>
              <div className="lpv2__tpg-bullet">Rack elevation planner with 1U / 2U / 4U mounting &amp; weight balance</div>
              <div className="lpv2__tpg-bullet">Share-by-invite live collaboration — multi-user editing on the same canvas</div>
              <div className="lpv2__tpg-bullet">Excel device library import / export, PDF wiring report with cable schedule</div>
            </div>
            <Link to="/tools/av-designer" className="lpv2__tpg-cta" data-testid="landing-avd-cta-btn">
              Open AV System Designer <ArrowRight size={16} />
            </Link>
          </div>
          <div
            className="lpv2__tpg-preview"
            onClick={() => setLightbox({ src: '/landing/avd-signal-flow.png', alt: 'AV System Designer — signal flow canvas' })}
            style={{ cursor: 'zoom-in', aspectRatio: '1 / 1' }}
            data-testid="landing-avd-preview"
          >
            <img
              src="/landing/avd-signal-flow.png"
              alt="AV System Designer signal flow showing Disguise VX 4, Barco E3, Dataton Watchout 7 server, NovaStar MX40 Pro, Dell P2222H monitors and LED screen cabinet wired together."
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </section>

      {/* ---------- Pattern & Blend standalone CTA ---------- */}
      <section className="lpv2__section" aria-labelledby="tpg-heading">
        <div className="lpv2__tpg" data-testid="landing-tpg-cta">
          <div className="lpv2__tpg-copy">
            <div className="lpv2__tpg-eyebrow">New · Standalone Utility</div>
            <h2 id="tpg-heading">
              Pattern &amp; <span style={{
                background: 'var(--brand-grad)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent'
              }}>Blend</span>
            </h2>
            <p>
              The on-site calibration tool engineers actually trust. Plan a multi-projector array,
              auto-calculate edge overlaps and lux, drop the red projected-size mask over a grid
              target — then export pixel-exact PNGs or a client-ready PDF report with your own logo
              before the projectors even arrive at the venue.
            </p>
            <div className="lpv2__tpg-bullets">
              <div className="lpv2__tpg-bullet">12 industry-standard patterns · grid · blend · focus · LED · geometry</div>
              <div className="lpv2__tpg-bullet">Multi-projector edge blending with per-tile offsets &amp; auto-calc</div>
              <div className="lpv2__tpg-bullet">Up to 8K resolution · safe-area guides · corner &amp; edge markers</div>
              <div className="lpv2__tpg-bullet">PDF calibration report with AVST logo + your own watermark</div>
              <div className="lpv2__tpg-bullet">Zero impact on the 3D editor — runs in its own lightweight page</div>
            </div>
            <Link to="/tools/test-pattern" className="lpv2__tpg-cta" data-testid="landing-tpg-cta-btn">
              Open Pattern &amp; Blend <ArrowRight size={16} />
            </Link>
          </div>
          <div
            className="lpv2__tpg-preview"
            onClick={() => setLightbox({ src: '/landing/pattern-blend-preview.png', alt: 'Pattern & Blend — 6-projector edge-blended grid showing overlap regions, per-tile resolution stamps and AV Simulation Tool watermark.' })}
            style={{ cursor: 'zoom-in', aspectRatio: '1 / 1' }}
            data-testid="landing-tpg-preview"
          >
            <img
              src="/landing/pattern-blend-preview.png"
              alt="Pattern & Blend preview — 6-projector edge-blended grid showing pink overlap regions, per-tile resolution stamps (1920×1080 / 5.33 × 3.00 m), and AV Simulation Tool watermark."
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </section>

      {/* ---------- Final CTA ---------- */}
      <section id="cta" className="lpv2__cta-final">
        <Reveal><div className="lpv2__eyebrow">Ready when you are</div></Reveal>
        <Reveal as="h2" delay={60}>
          Start designing your AV setup <span className="grad" style={{
            background: 'var(--brand-grad)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent'
          }}>today.</span>
        </Reveal>
        <Reveal as="p" delay={140}>
          Start your free 15-day demo. Everything unlocked. No install, no plugins, no payment required.
        </Reveal>
        <Reveal delay={220}>
          <Link to="/signup" className="lpv2__btn lpv2__btn--primary" data-testid="landing-cta-final">
            Start 15-Day Free Demo <ArrowRight size={16} />
          </Link>
        </Reveal>
      </section>

      <footer className="lpv2__footer" data-testid="landing-footer">
        <div className="lpv2__footer-row">
          <a
            href="mailto:admin@ideationav.com"
            className="lpv2__footer-link"
            data-testid="landing-footer-contact"
          >
            <Mail size={14} /> admin@ideationav.com
          </a>
          <a
            href="https://www.instagram.com/avsimulationtool/"
            target="_blank"
            rel="noreferrer"
            className="lpv2__footer-link"
            data-testid="landing-footer-instagram"
            aria-label="Instagram · @avsimulationtool"
          >
            <Instagram size={14} /> @avsimulationtool
          </a>
          <Link
            to="/terms"
            className="lpv2__footer-link"
            data-testid="landing-footer-terms"
          >
            Terms &amp; Conditions
          </Link>
        </div>
        <div className="lpv2__footer-row lpv2__footer-row--meta">
          <span>© {new Date().getFullYear()} AV Simulation Tool · Design · Simulate · Calibrate</span>
          <span>
            Powered by{' '}
            <a
              href="https://www.ideationav.com"
              target="_blank"
              rel="noreferrer"
              className="lpv2__footer-powered"
              data-testid="landing-footer-powered"
            >
              Ideation AV
            </a>
          </span>
        </div>
      </footer>

      {lightbox && <Lightbox {...lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
};

export default LandingPageV2;
